import { get, patch, post } from "@/infra/rest";
import type {
  AnswerBusinessOwnerQuestionResponse,
  BusinessOwnerAuthResponse,
  BusinessOwnerForm,
  CreateBusinessOwnerFormResponse,
  IntakeFormQuestionV2,
  StateChangeAction,
} from "../types/intake-form-v2";

export interface FetchBusinessOwnerFormResponse {
  business_owner_form: BusinessOwnerForm;
  questions: IntakeFormQuestionV2[];
}

export interface ChangeStatePayload {
  action: StateChangeAction;
  assessment_id?: string;
  rejection_reason?: string;
}

export async function fetchBusinessOwnerForm(
  formId: string,
): Promise<FetchBusinessOwnerFormResponse> {
  return get<FetchBusinessOwnerFormResponse>({
    url: `/v0/tpra/intake-form-v2/business-owner/${formId}`,
  });
}

export async function changeBusinessOwnerFormState(
  formId: string,
  payload: ChangeStatePayload,
): Promise<void> {
  return patch<void>({
    url: `/v0/tpra/intake-form-v2/business-owner/${formId}/state`,
    config: { data: payload },
  });
}

export async function authenticateBusinessOwner(
  token: string,
): Promise<BusinessOwnerAuthResponse> {
  return post<undefined, BusinessOwnerAuthResponse>({
    url: "/v0/tpra/intake-form-v2/business-owner/authenticate",
    data: undefined,
    config: {
      headers: { token },
      withCredentials: true,
    },
    isAuthRequired: false,
  });
}

export async function createBusinessOwnerForm(
  payload: { vendor_name: string; product_name: string | null },
): Promise<CreateBusinessOwnerFormResponse> {
  return post<
    { vendor_name: string; product_name: string | null },
    CreateBusinessOwnerFormResponse
  >({
    url: "/v0/tpra/intake-form-v2/business-owner",
    data: payload,
    config: { withCredentials: true },
    isAuthRequired: false,
  });
}

export async function fetchBusinessOwnerFormExternal(
  formId: string,
): Promise<FetchBusinessOwnerFormResponse> {
  return get<FetchBusinessOwnerFormResponse>({
    url: `/v0/tpra/intake-form-v2/business-owner/${formId}`,
    config: { withCredentials: true },
    isAuthRequired: false,
  });
}

export async function answerBusinessOwnerQuestion(
  formId: string,
  questionId: string,
  payload: { response: string | string[]; source_urls?: string[] },
): Promise<AnswerBusinessOwnerQuestionResponse> {
  return patch<AnswerBusinessOwnerQuestionResponse>({
    url: `/v0/tpra/intake-form-v2/business-owner/${formId}/questions/${questionId}`,
    config: { data: payload, withCredentials: true },
    isAuthRequired: false,
  });
}

export async function fetchIntakeFormForReview(
  formId: string,
): Promise<FetchBusinessOwnerFormResponse> {
  return get<FetchBusinessOwnerFormResponse>({
    url: `/v0/tpra/intake-form-v2/${formId}`,
  });
}

export async function completeAssessmentIntake(
  assessmentId: string,
): Promise<{ success: boolean }> {
  return post<undefined, { success: boolean }>({
    url: `/v0/tpra/intake-form-v2/assessments/${assessmentId}/complete`,
    data: undefined,
  });
}

export interface FetchAssessmentIntakeFormResponse {
  success: boolean;
  questions: IntakeFormQuestionV2[];
}

export async function fetchAssessmentIntakeForm(
  assessmentId: string,
): Promise<FetchAssessmentIntakeFormResponse> {
  return get<FetchAssessmentIntakeFormResponse>({
    url: `/v0/tpra/intake-form-v2/assessments/${assessmentId}`,
  });
}

export async function saveAssessmentIntakeQuestion(
  assessmentId: string,
  questionId: string,
  payload: {
    response: string | string[] | null;
    source_urls?: string[] | null;
  },
): Promise<{ success: boolean; question: IntakeFormQuestionV2 }> {
  return patch<{ success: boolean; question: IntakeFormQuestionV2 }>({
    url: `/v0/tpra/intake-form-v2/assessments/${assessmentId}/questions/${questionId}`,
    config: { data: payload },
  });
}

export async function submitBusinessOwnerForm(
  formId: string,
): Promise<{ success: boolean; business_owner_form: BusinessOwnerForm }> {
  return post<
    undefined,
    { success: boolean; business_owner_form: BusinessOwnerForm }
  >({
    url: `/v0/tpra/intake-form-v2/business-owner/${formId}/submit`,
    data: undefined,
    config: { withCredentials: true },
    isAuthRequired: false,
  });
}

export interface AssessmentTierResponse {
  success: boolean;
  tier_number: 1 | 2 | 3 | 4;
  tier_name: "Critical" | "High" | "Medium" | "Low";
  tier_description: string;
  number_of_controls: number;
  number_of_questions: number;
  number_of_documents: number;
}

export async function fetchAssessmentTier(
  assessmentId: string,
): Promise<AssessmentTierResponse> {
  return get<AssessmentTierResponse>({
    url: `/v0/tpra/intake-form-v2/${assessmentId}/tier`,
  });
}

export async function updateAssessmentTier(
  assessmentId: string,
  tierValue: number,
): Promise<AssessmentTierResponse> {
  return patch<AssessmentTierResponse>({
    url: `/v0/tpra/intake-form-v2/assessments/${assessmentId}/tier/${tierValue}`,
  });
}

export interface MarkVendorFilledPayload {
  vendor_email_address: string;
  contact_email_address: string;
  contact_name: string;
}

export async function markAssessmentVendorFilled(
  assessmentId: string,
  payload: MarkVendorFilledPayload,
): Promise<{ success: boolean }> {
  return post<MarkVendorFilledPayload, { success: boolean }>({
    url: `/v0/tpra/assessments/${assessmentId}/mark-vendor-filled`,
    data: payload,
  });
}
